import { useMemo, useState } from 'react';
import type { DocumentRenderAttachmentSummary, DocumentRenderSection } from '@vivport/backend/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ObservationCapture } from '@/components/widget/observation-capture/observation-capture';
import { assertToolSucceeded } from '@/api/templates-api';
import { useAddTableRow } from '@/hooks/useTemplateQueries';
import { useCaptureFlowStore } from '@/store/useCaptureFlowStore';
import { Loader2, Plus } from 'lucide-react';
import { cleanCell, parseSectionTables } from './section-tables';
import { hasMatchingRow } from './entry-write-guard';
import { EMPTY_CONTROL_STATE, resolveControlValue, type FieldControlState } from './field-control-state';
import { TemplateFieldControl } from './TemplateFieldControl';
import { useCaptureWrite } from '@/hooks/useCaptureWrite';
import { InlineRetryError } from '@/components/ui/inline-retry-error';
import { getDocumentRender } from '@/api/content-api';

interface TemplateAddTableRowDialogProps {
  section: DocumentRenderSection;
  instanceRootId: number;
  rootPhotos: DocumentRenderAttachmentSummary[];
}

function newSessionId(): string {
  return `add-row-${crypto.randomUUID()}`;
}

// Column order and the photo column come from the render payload and the body's own header row;
// control types and options come from the definition's column fields. The row is written by
// templates.addTableRow, which validates every cell — the dialog only disables an empty submit.
export function TemplateAddTableRowDialog({ section, instanceRootId, rootPhotos }: TemplateAddTableRowDialogProps) {
  const facts = section.templateSection;
  const repeatTables = useMemo(
    () => facts?.tables.filter((table) => table.mode === 'repeat') ?? [],
    [facts]
  );
  const [open, setOpen] = useState(false);
  const [tableBlockId, setTableBlockId] = useState<string>(() => String(repeatTables[0]?.blockId ?? ''));
  const [drafts, setDrafts] = useState<Record<string, FieldControlState>>({});
  const [photoId, setPhotoId] = useState<string>('');
  const [capturing, setCapturing] = useState(false);
  const [captureSessionId, setCaptureSessionId] = useState(newSessionId);
  const captureWrite = useCaptureWrite();
  const endSession = useCaptureFlowStore((state) => state.endSession);
  const addTableRow = useAddTableRow();
  const pending = captureWrite.pending;

  if (facts === null || repeatTables.length === 0) return null;

  const table = repeatTables.find((entry) => String(entry.blockId) === tableBlockId) ?? repeatTables[0];
  const ordinal = facts.tables.findIndex((entry) => entry.blockId === table.blockId);
  const bodyTable = parseSectionTables(section.description)[ordinal];
  const headers = bodyTable?.header.map(cleanCell) ?? [];
  const photoColumn = table.photoColumn === null ? null : cleanCell(table.photoColumn);
  const columnFields = facts.fields.filter(
    (field) => field.placement.kind === 'column' && field.placement.tableBlockId === table.blockId
  );
  const photos = [...section.otherAttachments, ...rootPhotos]
    .filter((item) => item.contentType === 'PHOTO')
    .filter((item, index, all) => all.findIndex((other) => other.id === item.id) === index);

  const hasValue =
    photoId.length > 0 || Object.values(drafts).some((draft) => resolveControlValue(draft).length > 0);

  const reset = () => {
    setDrafts({});
    setPhotoId('');
    captureWrite.clear();
    if (capturing) {
      endSession(captureSessionId);
      setCaptureSessionId(newSessionId());
      setCapturing(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    setOpen(next);
  };

  const submit = (keepOpen: boolean) => {
    const values: Record<string, string> = {};
    for (const [header, draft] of Object.entries(drafts)) {
      const value = resolveControlValue(draft);
      if (value.length > 0) values[header] = value;
    }
    const candidateCells = headers.map((header) =>
      photoColumn !== null && header === photoColumn ? null : values[header] ?? ''
    );
    void captureWrite.run({
      label: 'templates.addTableRow',
      write: async () =>
        assertToolSucceeded(
          await addTableRow.mutateAsync({
            instanceId: instanceRootId,
            sectionCode: section.code,
            values,
            ...(photoId.length > 0 ? { photoId: Number(photoId) } : {}),
            ...(repeatTables.length > 1 ? { tableBlockId: table.blockId } : {}),
          })
        ),
      alreadyWritten: async () => {
        const fresh = await getDocumentRender(instanceRootId);
        const freshSection = fresh.sections.find((item) => item.code === section.code);
        if (freshSection === undefined) return false;
        return hasMatchingRow(parseSectionTables(freshSection.description)[ordinal], candidateCells);
      },
      onWritten: () => (keepOpen ? reset() : handleOpenChange(false)),
    });
  };

  return (
    <div className="mt-3">
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Plus className="mr-1.5 h-4 w-4" /> Add row
      </Button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent compactFullScreen>
          <DialogHeader>
            <DialogTitle>Add row</DialogTitle>
            <DialogDescription>
              One row for {section.title || section.code}. Columns left empty stay empty.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-3">
            {repeatTables.length > 1 ? (
              <div className="space-y-1.5">
                <Label>Table</Label>
                <Select value={tableBlockId} onValueChange={setTableBlockId}>
                  <SelectTrigger className="h-8 max-sm:h-12 max-sm:text-base">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {repeatTables.map((entry) => (
                      <SelectItem key={entry.blockId} value={String(entry.blockId)} className="max-sm:min-h-12 max-sm:text-base">
                        Table {entry.blockId}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            {bodyTable === undefined ? (
              <p className="text-sm text-destructive">This section no longer holds its table; edit the text directly.</p>
            ) : null}
            {headers.map((header) => {
              const controlId = `add-row-${section.id}-${header}`;
              if (photoColumn !== null && header === photoColumn) {
                return (
                  <div key={header} className="space-y-1.5">
                    <Label htmlFor={controlId}>{header}</Label>
                    <div className="flex items-center gap-2">
                      <Select value={photoId} onValueChange={setPhotoId} disabled={pending}>
                        <SelectTrigger id={controlId} className="h-8 max-sm:h-12 max-sm:text-base">
                          <SelectValue placeholder={photos.length > 0 ? 'Choose a photo…' : 'No photos attached yet'} />
                        </SelectTrigger>
                        <SelectContent>
                          {photos.map((photo) => (
                            <SelectItem key={photo.id} value={String(photo.id)} className="max-sm:min-h-12 max-sm:text-base">
                              {photo.title.length > 0 ? photo.title : photo.code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setCapturing((value) => !value)}
                        disabled={pending}
                      >
                        Capture
                      </Button>
                    </div>
                    {capturing ? (
                      <ObservationCapture
                        sessionId={captureSessionId}
                        defaultMode="photo"
                        linkContentId={section.id}
                        onSubmitted={({ photoIds }) => {
                          if (photoIds[0] !== undefined) setPhotoId(String(photoIds[0]));
                        }}
                      />
                    ) : null}
                  </div>
                );
              }
              const field = columnFields.find(
                (entry) => entry.placement.kind === 'column' && cleanCell(entry.placement.column) === header
              );
              return (
                <div key={header} className="space-y-1.5">
                  <Label htmlFor={controlId}>{header}</Label>
                  <TemplateFieldControl
                    id={controlId}
                    spec={field ?? { type: 'text' }}
                    state={drafts[header] ?? EMPTY_CONTROL_STATE}
                    onChange={(state) => {
                      captureWrite.clear();
                      setDrafts((prev) => ({ ...prev, [header]: state }));
                    }}
                    disabled={pending}
                  />
                </div>
              );
            })}
            {captureWrite.error !== null ? (
              <InlineRetryError message={captureWrite.error} onRetry={captureWrite.retry} disabled={pending} />
            ) : null}
          </DialogBody>
          <DialogFooter className="max-sm:sticky max-sm:bottom-0 max-sm:-mx-4 max-sm:-mb-4 max-sm:border-t max-sm:bg-background max-sm:px-4 max-sm:pb-safe max-sm:pt-3">
            <Button variant="ghost" onClick={() => handleOpenChange(false)} disabled={pending}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={() => submit(true)}
              disabled={!hasValue || pending || bodyTable === undefined}
            >
              {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}Save and add another
            </Button>
            <Button onClick={() => submit(false)} disabled={!hasValue || pending || bodyTable === undefined}>
              {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
